import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n } from '../../i18n';
import { useApiAuth } from '../../lib/useApi';
import { useProfile } from '../../state/profile';
import { LeaderRow } from '../../components/boards/LeaderRow';
import {
  createLeague,
  getLeaderboard,
  joinLeague,
  listLeagues,
  type LeaderboardEntry,
  type League,
} from '../../lib/boards';
import type { ApiError } from '../../lib/http';

const PAGE = 20;
type Scope = 'swiss' | 'canton' | 'leagues';

export function Boards() {
  return (
    <RequireOnboarded>
      <AppLayout>
        <BoardsInner />
      </AppLayout>
    </RequireOnboarded>
  );
}

function BoardsInner() {
  const { t } = useI18n();
  const { profile } = useProfile();
  const [scope, setScope] = useState<Scope>('swiss');

  return (
    <>
      <div class="bc-phasetabs">
        <button class={`bc-pill${scope === 'swiss' ? ' is-active' : ''}`} onClick={() => setScope('swiss')}>
          {t('boards.swiss')}
        </button>
        {profile?.canton && (
          <button class={`bc-pill${scope === 'canton' ? ' is-active' : ''}`} onClick={() => setScope('canton')}>
            {profile.canton}
          </button>
        )}
        <button class={`bc-pill${scope === 'leagues' ? ' is-active' : ''}`} onClick={() => setScope('leagues')}>
          {t('boards.leagues')}
        </button>
      </div>

      {scope === 'leagues' ? (
        <LeaguesPanel />
      ) : (
        <Ranking scope={scope} code={scope === 'canton' ? profile?.canton : undefined} />
      )}
    </>
  );
}

function Ranking({ scope, code }: { scope: 'swiss' | 'canton'; code?: string }) {
  const { t } = useI18n();
  const auth = useApiAuth();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [me, setMe] = useState<LeaderboardEntry | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getLeaderboard(auth, { scope, code, limit: PAGE, offset: 0, search: search || undefined })
      .then((d) => {
        if (!alive) return;
        setEntries(d.entries);
        setMe(d.me ?? null);
        setTotal(d.total);
      })
      .catch(() => alive && setEntries([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [scope, code, search]);

  async function loadMore() {
    const d = await getLeaderboard(auth, {
      scope,
      code,
      limit: PAGE,
      offset: entries.length,
      search: search || undefined,
    });
    setEntries((prev) => [...prev, ...d.entries]);
  }

  const meInList = me && entries.some((e) => e.userId === me.userId);

  return (
    <>
      <input
        class="bc-input bc-search"
        placeholder={t('boards.search')}
        value={search}
        onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
      />
      {loading ? (
        <div class="bc-spinner">{t('app.loading')}</div>
      ) : entries.length === 0 ? (
        <p class="bc-empty">{t('boards.empty')}</p>
      ) : (
        <>
          <div class="lr-list">
            {entries.map((e) => (
              <LeaderRow key={e.userId} e={e} />
            ))}
          </div>
          {entries.length < total && (
            <button class="bc-pill bc-loadmore" onClick={loadMore}>
              {t('boards.load_more')}
            </button>
          )}
          {me && !meInList && (
            <div class="lr-me-sticky">
              <LeaderRow e={{ ...me, me: true }} />
            </div>
          )}
        </>
      )}
    </>
  );
}

function LeaguesPanel() {
  const { t, localePath } = useI18n();
  const auth = useApiAuth();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState<'none' | 'create' | 'join'>('none');

  function refresh() {
    listLeagues(auth)
      .then(setLeagues)
      .catch(() => setLeagues([]))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  return (
    <>
      <div class="lg-actions">
        <button class="bc-pill" onClick={() => setSheet('create')}>{t('boards.create')}</button>
        <button class="bc-pill" onClick={() => setSheet('join')}>{t('boards.join')}</button>
      </div>

      {loading ? (
        <div class="bc-spinner">{t('app.loading')}</div>
      ) : leagues.length === 0 ? (
        <p class="bc-empty">{t('boards.no_leagues')}</p>
      ) : (
        <div class="lg-list">
          {leagues.map((lg) => (
            <a key={lg.id} class="lg-card" href={localePath(`/boards/league/${lg.id}`)}>
              <span class="lg-card__emoji">{lg.emoji ?? '🏆'}</span>
              <span class="lg-card__body">
                <strong>{lg.name}</strong>
                <small>{lg.members} {t('boards.members')}</small>
              </span>
            </a>
          ))}
        </div>
      )}

      {sheet === 'create' && (
        <CreateSheet onClose={() => setSheet('none')} onDone={() => { setSheet('none'); setLoading(true); refresh(); }} />
      )}
      {sheet === 'join' && (
        <JoinSheet onClose={() => setSheet('none')} onDone={() => { setSheet('none'); setLoading(true); refresh(); }} />
      )}
    </>
  );
}

function CreateSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const auth = useApiAuth();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🏆');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function go() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setErr(false);
    try {
      await createLeague(auth, { name: name.trim(), emoji });
      onDone();
    } catch {
      setErr(true);
      setBusy(false);
    }
  }
  return (
    <Sheet title={t('boards.create_title')} onClose={onClose}>
      <input class="bc-input" placeholder={t('boards.league_name')} value={name} maxLength={64}
        onInput={(e) => setName((e.target as HTMLInputElement).value)} />
      <input class="bc-input" placeholder="🏆" value={emoji} maxLength={4}
        onInput={(e) => setEmoji((e.target as HTMLInputElement).value)} />
      {err && <p class="onb-err">{t('boards.error')}</p>}
      <button class="bc-cta" disabled={!name.trim() || busy} onClick={go}>
        {busy ? t('onb.saving') : t('boards.create')}
      </button>
    </Sheet>
  );
}

// Invite codes are 12 alphanumeric chars; we display them grouped in fours
// (XXXX-XXXX-XXXX). rawCode strips the formatting back to what the API wants.
function rawCode(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
}
function formatCode(s: string): string {
  return rawCode(s).replace(/(.{4})(?=.)/g, '$1-');
}

function JoinSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const auth = useApiAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const raw = rawCode(code);

  async function go() {
    if (raw.length !== 12 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await joinLeague(auth, raw);
      onDone();
    } catch (e) {
      setErr((e as ApiError).status === 404 ? t('boards.join_notfound') : t('boards.error'));
      setBusy(false);
    }
  }
  return (
    <Sheet title={t('boards.join_title')} onClose={onClose}>
      {/* maxLength 14 = 12 chars + 2 dashes; rawCode caps real chars at 12 */}
      <input class="bc-input" placeholder={t('boards.invite_code')} value={code} maxLength={14}
        autocapitalize="characters" autocomplete="off" spellcheck={false}
        onInput={(e) => setCode(formatCode((e.target as HTMLInputElement).value))} />
      {err && <p class="onb-err">{err}</p>}
      <button class="bc-cta" disabled={raw.length !== 12 || busy} onClick={go}>
        {busy ? t('onb.saving') : t('boards.join')}
      </button>
    </Sheet>
  );
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  return (
    <div class="bc-sheet__scrim" onClick={onClose}>
      <div class="bc-sheet" onClick={(e) => e.stopPropagation()}>
        <h2 class="bc-sheet__title">{title}</h2>
        {children}
      </div>
    </div>
  );
}
